import { useMemo, useCallback, useRef, useEffect } from "react";
import capitalize from "lodash/capitalize";
import { toast } from "@jsq/ds/core";
import { withClientContext } from "~/apollo/client";
import { useGetCcToolsUsersQuery, useGetWorkspacesQuery } from "~/graphql/mainHooks";
import { useGetWorkspaceRolesQuery } from "~/graphql/supergraphHooks";
import { useDateFormatterWithTimezone } from "~/pages/UserAndRoles/utils/dateFormatter";
import { toDbId } from "~/utils/graphql-helpers";

const PAGE_SIZE = 3000; // hardcoded to max limit for now

export type FetchWorkspaceParams = {
  sortByName?: boolean;
  searchTerm?: string;
  first?: number;
  last?: number;
  after?: string;
  before?: string;
};

export const useFetchWorkspaces = ({
  last,
  after,
  before,
  sortByName,
  searchTerm,
}: FetchWorkspaceParams) => {
  const { formatDateWithTimezone } = useDateFormatterWithTimezone();
  const useGetWorkspacesQueryResult = useGetWorkspacesQuery({
    variables: {
      sortByName,
      searchTerm,
      first: PAGE_SIZE,
      last,
      after,
      before,
    },
  });

  const isLoadingRef = useRef(false);
  const loadAllWorkspaces = useCallback(async () => {
    if (isLoadingRef.current) {
      return;
    }

    isLoadingRef.current = true;

    const loadNextPage = async (cursor: string | null, hasMore: boolean): Promise<void> => {
      if (!hasMore) {
        isLoadingRef.current = false;
        return;
      }

      try {
        const result = await useGetWorkspacesQueryResult.fetchMore({
          variables: {
            sortByName,
            searchTerm,
            first: PAGE_SIZE,
            after: cursor,
          },
        });

        const nextHasMore = result.data?.arenas?.pageInfo.hasNextPage ?? false;
        const nextCursor = result.data?.arenas?.pageInfo.endCursor ?? null;

        if (nextHasMore) {
          await loadNextPage(nextCursor, nextHasMore);
        } else {
          isLoadingRef.current = false;
        }
      } catch {
        isLoadingRef.current = false;
        toast.error("Error loading workspaces");
      }
    };

    const hasNextPage = useGetWorkspacesQueryResult.data?.arenas?.pageInfo.hasNextPage ?? false;
    const endCursor = useGetWorkspacesQueryResult.data?.arenas?.pageInfo.endCursor ?? null;

    if (hasNextPage) {
      await loadNextPage(endCursor, hasNextPage);
    } else {
      isLoadingRef.current = false;
    }
  }, [useGetWorkspacesQueryResult, sortByName, searchTerm]);

  useEffect(() => {
    if (useGetWorkspacesQueryResult.data?.arenas?.pageInfo.hasNextPage) {
      void loadAllWorkspaces();
    }
  }, [useGetWorkspacesQueryResult.data?.arenas?.pageInfo.hasNextPage, loadAllWorkspaces]);

  const response = useMemo(() => {
    const workspaces = useGetWorkspacesQueryResult.data?.arenas?.edges.map((edge) => ({
      id: toDbId(edge.node.id),
      name: capitalize(edge.node.name),
      aboutText: edge.node.aboutText ?? "",
      jsqHostname: edge.node.jsqHostname ?? "",
      assignedUsersCount: edge.node.assignedUsersCount ?? 0,
      lastAccessTimestamp: formatDateWithTimezone(edge.node.lastAccessTimestamp ?? undefined),
    }));

    const count = useGetWorkspacesQueryResult.data?.arenas?.count;
    const pageInfo = useGetWorkspacesQueryResult.data?.arenas?.pageInfo;

    return { workspaces, count, pageInfo };
  }, [useGetWorkspacesQueryResult.data, formatDateWithTimezone]);

  return {
    rowData: response.workspaces ?? [],
    count: response.count,
    pageInfo: response.pageInfo,
    useGetWorkspacesQueryResult,
  };
};

export const useFetchCCToolsUsers = () => {
  const useFetchCCToolsUsersResult = useGetCcToolsUsersQuery({
    context: withClientContext("staff"),
  });

  const rowData = useMemo(() => {
    return useFetchCCToolsUsersResult.data?.usersCcTools?.edges.map((e) => ({
      id: toDbId(e.node.id),
      name: e.node.fullName || "--",
      email: e.node.emailAddress,
    }));
  }, [useFetchCCToolsUsersResult.data]);

  return { users: rowData ?? [], useFetchCCToolsUsersResult };
};

export const useFetchWorkspaceRoles = () => {
  const useFetchWorkspaceRolesResult = useGetWorkspaceRolesQuery();

  const rowData = useMemo(() => {
    if (
      useFetchWorkspaceRolesResult.data?.arenaAccessByRoleGroups?.__typename !==
      "ArenaAccessByRoleGroupsOutput"
    ) {
      return [];
    }

    return useFetchWorkspaceRolesResult.data?.arenaAccessByRoleGroups?.countByRoles.map(
      (item) => ({
        role: item.roleGroupName,
        assignedUsers: item.count,
      }),
    );
  }, [useFetchWorkspaceRolesResult.data]);

  return { roles: rowData, useFetchWorkspaceRolesResult };
};
